import {
  Typography,
  Box,
  Table,
  TableHead,
  TableContainer,
  TableBody,
  TableRow,
  FormHelperText,
  FormControl,
  Grid,
  SvgIcon,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import DeleteIcon from "@mui/icons-material/Delete";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import * as yup from "yup";
import { useFormik } from "formik";
import { useGetSubjectsPaginatedQuery } from "../subjects/subjectsApiSlice";
import { useGetTeachersPaginatedQuery } from "../staff/staffApiSlice";
import {
  useAddSubjectToClassroomMutation,
  useGetClassroomWithSubjectsQuery,
  useRemoveSubjectFromClassroomMutation,
  useReplaceSubjectTeacherMutation,
} from "./classApiSlice";
import { useEffect, useState } from "react";
import toast from "../../../helpers/toast";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";

function ClassSubjects({ item, setItem }) {
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [focusedSubject, setFocusedSubject] = useState(null);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const { data: teachersData } = useGetTeachersPaginatedQuery({ page: 1, per_page: 10, q: teacherSearch });
  const teachers = teachersData?.teachers ?? [];
  const { data: subjectsData } = useGetSubjectsPaginatedQuery({ page: 1, per_page: 10, q: subjectSearch });
  const subjects = subjectsData?.subjects ?? [];
  const { data: classroomData } = useGetClassroomWithSubjectsQuery(item?.id, { skip: !item?.id }); //to get updated classroom with subject+teacher info

  useEffect(() => {
    if (!classroomData) return;
    setItem(classroomData);
  }, [classroomData]);

  const [addSubjectToClass, { isLoading: addSubjectToClassLoading }] =
    useAddSubjectToClassroomMutation();

  const [replaceSubjectTeacher, { isLoading: replaceSubjectTeacherLoading }] =
    useReplaceSubjectTeacherMutation();

  const [
    removeSubjectFromClassroom,
    { isLoading: removeSubjectFromClassroomLoading },
  ] = useRemoveSubjectFromClassroomMutation();

  const classForm = useFormik({
    initialValues: {
      teacher: focusedSubject?.teacher_id || "",
      subject: focusedSubject?.id || "",
    },
    validationSchema: yup.object({
      teacher: yup.string().required("Required"),
      subject: yup.string().required("Required"),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      const body = {
        class_Id: item.id,
        teacher_id: values.teacher,
        subject_id: values.subject,
      };
      if (focusedSubject) {
        handleReplaceSubjectTeacher(body);
      } else {
        handleAddSubjectToClass(body);
      }
    },
  });

  const handleAddSubjectToClass = async (body) => {
    try {
      const response = await addSubjectToClass(body).unwrap();
      if (response.message) {
        classForm.resetForm();
        toast.success("Subject added successfully");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const handleReplaceSubjectTeacher = async (body) => {
    try {
      const response = await replaceSubjectTeacher(body).unwrap();
      if (response.message) {
        classForm.resetForm();
        toast.success("Teacher Replaced successfully");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const handleRemoveSubject = async () => {
    const body = {
      class_Id: item.id,
      subject_Id: focusedSubject.id,
    };
    try {
      const response = await removeSubjectFromClassroom(body).unwrap();
      if (response.message) {
        toast.success(response.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };


  return (
    <StyledPaper>
      {/* modals */}
      <CustomWarningDialog
        open={openWarningDialog}
        setOpen={setOpenWarningDialog}
        item={focusedSubject}
        action={handleRemoveSubject}
        message="!!!WARNING!!! This will remove the subject from the class. Are you sure you want to continue?"
      />
      <Box className="paperHeader">
        <Typography variant="paperHeading">
          Current Subjects In Class
        </Typography>
      </Box>
      <Box className="paperBody">
        <form onSubmit={classForm.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">subject</Typography>
                <Autocomplete
                  options={subjects}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={subjects.find(s => s.id === classForm.values.subject) || null}
                  onChange={(_, value) => classForm.setFieldValue("subject", value?.id || "")}
                  onInputChange={(_, value, reason) => { if (reason === "input") setSubjectSearch(value); }}
                  filterOptions={(x) => x}
                  disabled={!!focusedSubject}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="Search subject..."
                      error={classForm.touched.subject && Boolean(classForm.errors.subject)}
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {classForm.touched.subject && classForm.errors.subject}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Teacher</Typography>
                <Autocomplete
                  options={teachers}
                  getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ""}
                  isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                  value={teachers.find(t => t.user_id === classForm.values.teacher) || null}
                  onChange={(_, value) => classForm.setFieldValue("teacher", value?.user_id || "")}
                  onInputChange={(_, value, reason) => { if (reason === "input") setTeacherSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="Search teacher..."
                      error={classForm.touched.teacher && Boolean(classForm.errors.teacher)}
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {classForm.touched.teacher && classForm.errors.teacher}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              md={2}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                mb: 0.5,
              }}
            >
              <StyledLoadingButton
                loading={addSubjectToClassLoading}
                type="submit"
              >
                {focusedSubject ? "Save" : "Add"}
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
      <TableContainer
        sx={{ pb: 4 }}
        onClick={() => {
          setFocusedSubject(null);
        }}
      >
        <Table sx={{ width: "100%", boxShadow: "none" }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Subject </StyledTableCell>
              <StyledTableCell>Teacher</StyledTableCell>
              <StyledTableCell align="right">edit</StyledTableCell>
              <StyledTableCell align="right">remove</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {item?.subjects?.map((subject) => (
              <StyledTableRow key={subject.id}>
                <StyledTableCell component="th" scope="row">
                  {subject.name}
                </StyledTableCell>
                <StyledTableCell component="th" scope="row">
                  {subject.teacher_name || "-"}
                </StyledTableCell>
                <StyledTableCell align="right">
                  <SvgIcon
                    className="tableCellLink"
                    component={EditIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFocusedSubject(subject);
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell align="right">
                  {removeSubjectFromClassroomLoading &&
                  focusedSubject === subject ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DeleteIcon
                      className="tableCellLink"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFocusedSubject(subject);
                        setOpenWarningDialog(true);
                      }}
                    />
                  )}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </StyledPaper>
  );
}

export default ClassSubjects;
